import { T } from '@transifex/react';
import { VStack } from '@vidahealth/vida-ui';
import { useRef } from 'react';
import { View } from 'react-native';

import { ContactSupport } from '#modules/ui/ContactSupport';
import { CtaButton } from '#modules/ui/CtaButton';
import { Screen, type ScreenHandle } from '#modules/ui/Screen';
import { ScreenHeading } from '#modules/ui/ScreenHeading';

import type { AssessmentPendingStateApi } from '../../../hooks/useAssessmentPendingState';
import { useShowContactSupport } from '../../../hooks/useShowContactSupport';
import type { ActiveQuestion } from '../../../utils/resolveQuestionChoices';

import { QuestionField } from './QuestionField';

// Extra breathing room above a scrolled-to invalid field, so it doesn't land
// flush against the top edge of the ScrollView's visible area.
const SCROLL_TO_FIELD_PADDING = 16;

export type GroupedQuestionsScreenProps = {
  questions: ActiveQuestion[];
  pending: AssessmentPendingStateApi;
  isSubmitting: boolean;
  testIDPrefix?: string;
  pageTitle?: string | null;
  pageDescription?: string | null;
  /**
   * Validates every question and, if they all pass, commits + hands off.
   * Returns the first invalid question's key so this screen — the only layer
   * that knows where each field sits — can scroll it into view.
   */
  onContinue: () => string | null;
};

/**
 * Every question of a `singleScreen` node on one scrollable screen: the page's
 * own title/description are the single `ScreenHeading`, each question's title
 * renders as a field label above its input, and one Continue validates the
 * whole page at once, showing every invalid field's own error and scrolling to
 * the first of them.
 */
export function GroupedQuestionsScreen({
  questions,
  pending,
  isSubmitting,
  testIDPrefix,
  pageTitle,
  pageDescription,
  onContinue,
}: GroupedQuestionsScreenProps) {
  const showContactSupport = useShowContactSupport();
  // An imperative handle onto the Screen's ScrollView, so Continue can scroll
  // a specific invalid field into view, plus each rendered question's
  // last-known Y offset within that scroll content — populated by `onLayout`
  // on each question's wrapper as React Native lays the screen out, and read
  // back only once Continue needs to jump to one of them.
  const screenRef = useRef<ScreenHandle>(null);
  const questionOffsetsRef = useRef<Record<string, number>>({});

  // Scrolls the question at `key` into view, using its last `onLayout`
  // offset. A no-op before that layout has fired (nothing recorded yet) —
  // acceptable because at that point the field is either already visible (the
  // screen only just mounted) or the layout pass is imminent.
  const scrollToQuestion = (key: string) => {
    const offset = questionOffsetsRef.current[key];
    if (offset === undefined) return;
    screenRef.current?.scrollToOffset(Math.max(offset - SCROLL_TO_FIELD_PADDING, 0));
  };

  const onContinuePress = () => {
    const firstInvalidKey = onContinue();
    if (firstInvalidKey !== null) scrollToQuestion(firstInvalidKey);
  };

  return (
    <Screen
      ref={screenRef}
      busy={isSubmitting}
      footer={
        <CtaButton
          isBusy={isSubmitting}
          isDisabled={isSubmitting}
          onPress={onContinuePress}
          testID={testIDPrefix ? `${testIDPrefix}-continue` : undefined}
        >
          <T _str='Continue' />
        </CtaButton>
      }
    >
      {/* '4xl' between questions vs. QuestionField's own tighter internal
          gap: a question's label needs to read as grouped with its own
          input, not evenly spaced from the question above and below it. */}
      <VStack space='4xl'>
        <ScreenHeading title={pageTitle ?? ''} subtitle={pageDescription ?? undefined} />
        {questions.map((question) => {
          const key = question.key;
          return (
            <View
              key={key}
              testID={testIDPrefix ? `${testIDPrefix}-${key}-section` : undefined}
              onLayout={(event) => {
                questionOffsetsRef.current[key] = event.nativeEvent.layout.y;
              }}
            >
              <QuestionField
                question={question}
                headingVariant='field'
                // Namespaced per question — unlike single-question mode
                // (where exactly one question's testIDs ever exist at once),
                // every question here renders simultaneously, and two of
                // them could otherwise derive the SAME testID (e.g. two
                // choice questions sharing a "Yes" option, or any two
                // non-repeatingList inputs, which both use a bare
                // `${testIDPrefix}-input`).
                testIDPrefix={testIDPrefix ? `${testIDPrefix}-${key}` : undefined}
                isSubmitting={isSubmitting}
                state={pending.fieldState(question)}
                handlers={pending.handlers(question, { inlineAddRow: true })}
              />
            </View>
          );
        })}

        {showContactSupport ? <ContactSupport showPhone /> : null}
      </VStack>
    </Screen>
  );
}
